import React from "react";
import { Clock, RotateCcw, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/shared/utils/cn";
import { isToday, isYesterday, parseISO, format } from "date-fns";
import type { Shift } from "../types";
import { formatTimeInZone, formatDateInZone } from "../utils/formatShiftTime";

interface ShiftsListProps {
  shifts: Shift[];
  isLoading: boolean;
  error: Error | null;
}

export function ShiftsList({ shifts, isLoading, error }: ShiftsListProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-pulse text-muted-foreground">Loading shifts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-destructive">
          Failed to load shifts: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      </div>
    );
  }

  if (shifts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <Clock className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No shifts found</p>
      </div>
    );
  }

  const getRelativeDateLabel = (dateString: string): string => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return "Today";
    }
    if (isYesterday(date)) {
      return "Yesterday";
    }
    return format(date, "EEEE"); // Full day name (Monday, Tuesday, etc.)
  };



  const getShiftStatus = (shift: Shift): { label: string; color: string } | null => {
    // Check if there are any manual times
    const hasManualArrivalTime = !!shift.manual_arrival_time;
    const hasManualEndTime = !!shift.manual_end_time;
    
    // If no manual times, no badge
    if (!hasManualArrivalTime && !hasManualEndTime) {
      return null;
    }
    
    // Check approval status - if approved_by is null/empty, it's not approved
    const arrivalApproved = !!shift.arrival_time_approved_by;
    const endApproved = !!shift.end_time_approved_by;
    
    // If both manual times exist and both are approved, show Approved
    if (hasManualArrivalTime && hasManualEndTime) {
      if (arrivalApproved && endApproved) {
        return { label: "Approved", color: "bg-emerald-100 text-emerald-700" };
      }
      return { label: "Pending", color: "bg-orange-100 text-orange-700" };
    }
    
    // If only arrival time exists
    if (hasManualArrivalTime) {
      if (arrivalApproved) {
        return { label: "Approved", color: "bg-emerald-100 text-emerald-700" };
      }
      return { label: "Pending", color: "bg-orange-100 text-orange-700" };
    }
    
    // If only end time exists
    if (hasManualEndTime) {
      if (endApproved) {
        return { label: "Approved", color: "bg-emerald-100 text-emerald-700" };
      }
      return { label: "Pending", color: "bg-orange-100 text-orange-700" };
    }
    
    return null;
  };

  return (
    <>
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <RotateCcw className="h-4 w-4 text-muted-foreground" />
        <span>Recent Shifts</span>
      </h3>
      <div className="space-y-3">
        {shifts.map((shift) => {
          const status = getShiftStatus(shift);
          const relativeDate = getRelativeDateLabel(shift.started_at);
          const fullDate = formatDateInZone(shift.started_at, shift.timezone);
          
          // Use manual times if available, otherwise use logged times
          const startTime = shift.manual_arrival_time || shift.started_at;
          const endTime = shift.manual_end_time || shift.ended_at;
          const tz = shift.timezone;
          const timeRange = endTime 
            ? `${formatTimeInZone(startTime, tz)} - ${formatTimeInZone(endTime, tz)}`
            : `${formatTimeInZone(startTime, tz)} - Present`;

          return (
            <div
              key={shift.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-3 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-800 m-0">{relativeDate}</h4>
                    {status && (
                      <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", status.color)}>
                        {status.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{fullDate}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span>{timeRange}</span>
                </div>
              </div>

              {shift.ended_at && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-end justify-end">
                  <button 
                    onClick={() => navigate(`/shifts/${shift.id}`)}
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    View Details
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

